import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { BUSINESS } from "@/lib/site";
import { PLANS } from "@/lib/plans";
import { Logo } from "@/components/site/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const topLevelLinks = [
  { to: "/", label: "Home", testId: "nav-link-home", end: true },
  { to: "/contact", label: "Contact", testId: "nav-link-contact" },
];

// Mobile flat list (Plans expanded inline)
const mobileLinks = [
  { to: "/", label: "Home", testId: "nav-link-home" },
  { to: "/aca-health", label: "ACA Health", testId: "nav-link-aca" },
  { to: "/medicare-advantage", label: "Medicare Advantage", testId: "nav-link-medicare-advantage" },
  { to: "/medicare-supplement", label: "Medicare Supplement", testId: "nav-link-medicare-supplement" },
  { to: "/dental", label: "Dental", testId: "nav-link-dental" },
  { to: "/hospital-indemnity", label: "Hospital Indemnity", testId: "nav-link-hospital-indemnity" },
  { to: "/cancer-plans", label: "Cancer Plans", testId: "nav-link-cancer-plans" },
  { to: "/long-term-care", label: "Long Term Care", testId: "nav-link-long-term-care" },
  { to: "/final-expense", label: "Final Expense", testId: "nav-link-final-expense" },
  { to: "/medicare-101", label: "Medicare 101", testId: "nav-link-medicare-101" },
  { to: "/short-term-medical", label: "Short Term Medical / ACA Alt", testId: "nav-link-short-term-medical" },
  { to: "/contact", label: "Contact", testId: "nav-link-contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex items-center gap-3 group"
          onClick={() => setOpen(false)}
          aria-label="30A Health Insurance home"
        >
          <Logo className="h-14 w-auto" />
          <span className="sr-only">30A Health Insurance</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <NavLink
            to="/"
            end
            data-testid="nav-link-home"
            className={({ isActive }) =>
              `px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors duration-200 ${
                isActive
                  ? "text-[var(--brand-primary)] font-semibold"
                  : "text-slate-600 hover:text-[var(--brand-primary)]"
              }`
            }
          >
            Home
          </NavLink>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="nav-plans-trigger"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md whitespace-nowrap text-slate-600 hover:text-[var(--brand-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/30 transition-colors"
            >
              Plans <ChevronDown className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              data-testid="nav-plans-menu"
              align="start"
              sideOffset={8}
              className="w-64 p-2"
            >
              {PLANS.map((p) => (
                <DropdownMenuItem key={p.slug} asChild>
                  <Link
                    to={`/${p.slug}`}
                    data-testid={`nav-link-${p.slug}`}
                    className="cursor-pointer text-sm text-slate-700 hover:text-[var(--brand-primary)]"
                  >
                    {p.name}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <NavLink
            to="/medicare-101"
            data-testid="nav-link-medicare-101"
            className={({ isActive }) =>
              `px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors duration-200 ${
                isActive
                  ? "text-[var(--brand-primary)] font-semibold"
                  : "text-slate-600 hover:text-[var(--brand-primary)]"
              }`
            }
          >
            Medicare 101
          </NavLink>

          <NavLink
            to="/short-term-medical"
            data-testid="nav-link-short-term-medical"
            className={({ isActive }) =>
              `px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors duration-200 ${
                isActive
                  ? "text-[var(--brand-primary)] font-semibold"
                  : "text-slate-600 hover:text-[var(--brand-primary)]"
              }`
            }
          >
            Short Term / ACA Alt
          </NavLink>

          <NavLink
            to="/contact"
            data-testid="nav-link-contact"
            className={({ isActive }) =>
              `px-3 py-2 text-sm rounded-md whitespace-nowrap transition-colors duration-200 ${
                isActive
                  ? "text-[var(--brand-primary)] font-semibold"
                  : "text-slate-600 hover:text-[var(--brand-primary)]"
              }`
            }
          >
            Contact
          </NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="header-call-link"
            className="hidden xl:flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[var(--brand-primary)] whitespace-nowrap transition-colors"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS.phone}
          </a>
          <Link
            to="/quote"
            data-testid="header-quote-cta"
            className="inline-flex items-center rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 py-2.5 whitespace-nowrap transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>

        <button
          type="button"
          aria-label="Toggle navigation"
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-5 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
            {mobileLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.testId}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-slate-100 text-[var(--brand-primary)] font-semibold"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              onClick={() => setOpen(false)}
              data-testid="mobile-quote-cta"
              className="mt-3 inline-flex items-center justify-center rounded-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white text-sm font-medium px-5 py-3 transition-colors"
            >
              Get a Free Quote
            </Link>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="mobile-call-link"
              className="mt-2 inline-flex items-center justify-center gap-1.5 text-sm text-slate-700 py-2"
            >
              <Phone className="w-4 h-4" />
              {BUSINESS.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
